import { LogOut, Eraser, RotateCcw, ShieldAlert, PackageCheck, ArrowUpCircle, SlidersHorizontal, Ban, Wrench, Globe, Info, AlertTriangle, ArrowUpRight, FileWarning } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { useSiteConfig } from "@/context/SiteConfig";

const SETTINGS_GUIDE = "https://docs.google.com/document/d/1UtbUm7ixBCOB_WgKDTHbm5Mb61FOemNxsqKlQ6i-_E4/edit?tab=t.0";

const Chip = ({ children }) => (
  <code className="inline-block rounded border border-[#2A201A] bg-black/40 px-2 py-0.5 font-mono text-xs text-teal">{children}</code>
);

const Warn = ({ children }) => (
  <div className="mt-4 flex items-start gap-3 border border-amber-500/30 bg-amber-500/10 p-4">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
    <p className="text-sm leading-relaxed text-amber-200/90">{children}</p>
  </div>
);

const Note = ({ children }) => (
  <div className="mt-4 flex items-start gap-3 border border-teal/25 bg-teal/5 p-4">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
    <p className="text-sm leading-relaxed text-[#B7AE9E]">{children}</p>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((b, i) => (
      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-[#B7AE9E]">
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-teal/60" />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

const Step = ({ n, icon: Icon, title, children }) => (
  <div className="border border-[#2A201A] bg-surface/40 p-6 sm:p-8" data-testid={`support-step-${n}`}>
    <div className="flex items-center gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-teal/30 bg-teal/10">
        <Icon className="h-5 w-5 text-teal" />
      </span>
      <h3 className="font-display text-xl sm:text-2xl font-light tracking-tight">
        <span className="mr-2 font-mono text-sm text-teal">{n}.</span>{title}
      </h3>
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

export default function Support() {
  useTitle("Technical Support | Aurora Springs RP | RedM Roleplay", "Common RedM fixes for crashing, freezing, infinite loading, texture and performance issues — cache clearing, drivers, firewall, DNS and our recommended settings guide for Aurora Springs RP.");
  const { img, link } = useSiteConfig();

  const settingsBtn = (
    <a href={SETTINGS_GUIDE} target="_blank" rel="noreferrer" data-testid="support-settings-btn"
      className="mt-5 inline-flex items-center gap-2 border border-teal/40 bg-teal/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-teal transition-colors hover:bg-teal/20">
      View Recommended RedM Settings <ArrowUpRight className="h-3.5 w-3.5" />
    </a>
  );

  return (
    <>
      <PageHeader overline="Technical Support" title="Common RedM Fixes" image={img("support", "/assets/support-hero.webp")}
        sub="These general troubleshooting steps resolve many common RedM issues, including crashing, freezing, audio problems, infinite loading screens, texture issues, and other unusual game behavior." />

      <div className="mx-auto max-w-4xl px-6 sm:px-10 py-14 space-y-6" data-testid="support-page">
        <Note>
          If this is your first time entering Aurora Springs, or you have recently cleared your RedM cache,
          please allow additional time for the County to load before assuming the game is stuck.
        </Note>

        <Step n={1} icon={LogOut} title="Log Out Completely">
          <Bullets items={[
            "Fully close RedM.",
            "Open the Rockstar Games Launcher and log out of your account.",
            "Restart your computer.",
            "Log back into Rockstar and launch RedM again.",
          ]} />
        </Step>

        <Step n={2} icon={Eraser} title="Clear Your RedM Cache">
          <p className="mt-4 text-sm leading-relaxed text-[#B7AE9E]">
            Clearing your cache helps resolve loading, texture, crashing, and other unusual RedM issues.
          </p>
          <Bullets items={[
            "Close RedM completely.",
            <>Go to <Chip>C:\Users\&lt;YourUsername&gt;\AppData\Local\RedM\RedM.app\</Chip></>,
            <>Delete all three folders: <Chip>cache</Chip> <Chip>server-cache</Chip> <Chip>server-cache-priv</Chip></>,
            "Restart your PC before relaunching RedM.",
          ]} />
          <Warn>Do NOT delete the entire RedM.app (RedM Application Data) folder — only the three cache folders inside it.</Warn>
          <div className="mt-6 border border-[#2A201A] bg-black/30 p-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-teal">Quick way to clear your cache</p>
            <ol className="mt-3 space-y-2">
              {[
                'Right-click your RedM desktop icon.',
                'Click "Open File Location".',
                'Find and open the "RedM Application Data" shortcut — it looks like a little snail icon.',
                "Go into the data folder.",
                "Delete the cache, server-cache and server-cache-priv folders inside.",
                "Restart your PC, then launch RedM again.",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-[#B7AE9E]">
                  <span className="shrink-0 font-mono text-xs text-teal">{i + 1}.</span><span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
          <Note>
            <span><strong className="text-[#E8E1D5]">Important:</strong> Your first connection to Aurora Springs after clearing your cache may take considerably longer while RedM downloads and rebuilds necessary files.</span>
          </Note>
        </Step>

        <Step n={3} icon={RotateCcw} title="Restart Your PC">
          <p className="mt-4 text-sm leading-relaxed text-[#B7AE9E]">A full restart can resolve:</p>
          <Bullets items={["Memory bugs", "Failed Rockstar sessions", "Cache not fully clearing", "Stuck processes"]} />
          <Note>Always restart after big fixes like clearing your cache or verifying game files.</Note>
        </Step>
      </div>

      {/* divider banner */}
      <div className="mx-auto max-w-4xl px-6 sm:px-10 pb-6" data-testid="support-divider">
        <div className="flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 p-5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <p className="font-mono text-xs uppercase tracking-[0.2em] leading-relaxed text-amber-200">
            If the first three steps do not work, try steps 4 through 9 — and be sure to restart your PC after each of those
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 sm:px-10 pb-14 space-y-6">
        <Step n={4} icon={ShieldAlert} title="Check Firewall & Antivirus">
          <Bullets items={[
            "Allow RedM.exe and RDR2.exe through Windows Firewall.",
            "Add both as exceptions in your antivirus software.",
            "You can temporarily disable your antivirus to test.",
          ]} />
        </Step>

        <Step n={5} icon={PackageCheck} title="Verify Your Game Files">
          <p className="mt-4 text-sm leading-relaxed text-[#B7AE9E]">
            Use the launcher where you own Red Dead Redemption 2 to verify or repair your game files.
          </p>
          <Bullets items={[
            "Open the Rockstar Games Launcher.",
            "Settings → Red Dead Redemption 2.",
            'Click "Verify Integrity" (or scan for issues).',
            "Let it finish, then restart your PC before trying RedM again.",
          ]} />
        </Step>

        <Step n={6} icon={ArrowUpCircle} title="Update Your Graphics Drivers">
          <Bullets items={[
            "Use NVIDIA GeForce Experience, AMD Adrenalin, or Intel's driver tool to check for updates.",
            'Choose "Clean Install" if the option is available.',
            "Restart your computer after installing new drivers.",
          ]} />
        </Step>

        <Step n={7} icon={SlidersHorizontal} title="Check Your Graphics Settings">
          <p className="mt-4 text-sm leading-relaxed text-[#B7AE9E]">
            Performance problems, texture issues, stuttering, and crashes may be caused or worsened by your graphics settings.
          </p>
          <Bullets items={[
            "Reduce resolution scaling.",
            "Lower volumetric lighting, shadows, or textures.",
            "Try windowed mode or disabling VSync.",
          ]} />
          <Note>We highly recommend following our Recommended RedM Settings Guide for tested Windows and in-game settings.</Note>
          {settingsBtn}
        </Step>

        <Step n={8} icon={Ban} title="Disable Overlays & Background Apps">
          <Bullets items={[
            "Turn off overlays such as Discord, Steam, NVIDIA (GeForce), or AMD overlays.",
            "Close unnecessary programs consuming significant CPU, GPU, or memory in Task Manager.",
          ]} />
        </Step>

        <Step n={9} icon={Wrench} title="Reinstall RedM (Last Resort)">
          <Bullets items={[
            "Uninstall RedM.",
            <>Delete the remaining folder: <Chip>C:\Users\&lt;YourUsername&gt;\AppData\Local\RedM</Chip></>,
            <>Download a fresh installation from the official site: <a href="https://redm.net" target="_blank" rel="noreferrer" className="text-teal underline underline-offset-4 hover:text-teal/80">redm.net</a></>,
            "Restart your PC before launching RedM again.",
          ]} />
        </Step>

        {/* Bonus: DNS */}
        <div className="border border-teal/25 bg-teal/5 p-6 sm:p-8" data-testid="support-dns">
          <div className="flex items-center gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-teal/30 bg-teal/10">
              <Globe className="h-5 w-5 text-teal" />
            </span>
            <h3 className="font-display text-xl sm:text-2xl font-light tracking-tight">Bonus: Flush Your DNS <span className="text-[#8F877C] text-lg">(Connection Fix)</span></h3>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[#B7AE9E]">
            If you're specifically experiencing connection problems, try flushing your DNS cache:
          </p>
          <Bullets items={["Open Command Prompt as Administrator.", "Run the command below.", "Restart your computer afterward."]} />
          <div className="mt-4 border border-[#2A201A] bg-black/50 px-4 py-3" data-testid="support-dns-cmd">
            <code className="font-mono text-sm text-teal">ipconfig /flushdns</code>
          </div>
        </div>

        {/* Performance section */}
        <section className="border border-[#2A201A] bg-surface/40 p-6 sm:p-8" data-testid="support-performance">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-teal">Going Deeper</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-light tracking-tight">Performance, Stuttering & FPS Issues</h2>
          <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#B7AE9E]">
            RedM is considerably more demanding than standard Red Dead Redemption 2, and some performance issues
            can be caused by graphics settings, Windows settings, drivers, or the capabilities of your computer.
          </p>
          <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#B7AE9E]">
            Aurora Springs has a Recommended RedM Settings Guide that covers:
          </p>
          <Bullets items={[
            "Recommended Windows settings for RedM",
            "High-performance and CPU priority settings",
            "Graphics driver and Windows updates",
            "Public DNS configuration",
            "RedM Release and Canary builds",
            "Recommended in-game graphics settings",
            "Dedicated GPU, mid-tier, laptop, and lower-end PC recommendations",
            "DLSS and advanced graphics settings",
          ]} />
          <p className="mt-5 text-sm leading-relaxed text-[#B7AE9E]">
            We highly recommend following this guide before{" "}
            <a href={link("techSupport")} target="_blank" rel="noreferrer" className="text-teal underline underline-offset-4 hover:text-teal/80">opening a Tech Support ticket</a>{" "}
            for low FPS, stuttering, head pops, texture issues, or general performance problems.
          </p>
          {settingsBtn}
        </section>

        {/* Crash dumps → Rage Error Ticket */}
        <section className="border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8" data-testid="support-crashdump">
          <div className="flex items-center gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-amber-500/30 bg-amber-500/10">
              <FileWarning className="h-5 w-5 text-amber-400" />
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Have a Crash Dump?</h2>
          </div>
          <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#B7AE9E]">
            If RedM crashed and left you with a RAGE error or crash dump, please open a Rage Error Ticket
            in our Discord and attach the dump so the team can dig into it.
          </p>
          <a href={link("rageTicket")} target="_blank" rel="noreferrer" data-testid="support-rage-btn"
            className="mt-5 inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-amber-300 transition-colors hover:bg-amber-500/20">
            Open a Rage Error Ticket <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </section>
      </div>
    </>
  );
}
